import React from "react";
import { motion } from "framer-motion";

export const GameStarSvg = ({ fillPercent = 1, uniqueId = "" }) => {
  const isHalf = fillPercent === 0.5;
  const isFull = fillPercent === 1;
  const isEmpty = fillPercent === 0;

  const gradId = "starGrad-" + uniqueId;
  const clipId = "starClip-" + uniqueId;
  const starPath =
    "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z";

  return (
    <svg
      viewBox="0 0 24 24"
      className="w-full h-full overflow-visible"
      style={{
        filter:
          "drop-shadow(0px 4px 0px rgba(180, 83, 9, 1)) drop-shadow(0px 8px 10px rgba(0,0,0,0.5))",
      }}
    >
      <defs>
        <linearGradient id={`${gradId}-full`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="35%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
        <linearGradient
          id={`${gradId}-empty`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#e2e8f0" stopOpacity="0.25" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x="0" y="0" width="12" height="24" />
        </clipPath>
      </defs>

      {/* Base (Empty) Star Layer */}
      <g>
        <path
          d={starPath}
          fill={`url(#${gradId}-empty)`}
          transform="translate(0, 1) scale(1.05)"
          style={{ transformOrigin: "center" }}
        />
        <path
          d={starPath}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="0.5"
        />
      </g>

      {/* Foreground (Full/Half) Star Layer */}
      {!isEmpty && (
        <g clipPath={isHalf ? `url(#${clipId})` : undefined}>
          <path
            d={starPath}
            fill={`url(#${gradId}-full)`}
            transform="translate(0, 1) scale(1.05)"
            style={{ transformOrigin: "center" }}
          />
          <path
            d={starPath}
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="0.5"
          />
          <path
            d={starPath}
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="0.3"
            transform="scale(0.9)"
            style={{ transformOrigin: "center" }}
          />
        </g>
      )}
    </svg>
  );
};

export const AnimatedStars = ({ value, starSize = 56 }) => {
  return (
    <div className="flex gap-2 mb-2 items-center justify-center pt-2 pb-2">
      {[0, 1, 2, 3, 4].map((index) => {
        let isFull = value >= index + 1;
        let isHalf = value >= index + 0.5 && value < index + 1;
        let fillPercent = isFull ? 1 : isHalf ? 0.5 : 0;
        const delay = index * 0.15;

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: delay,
            }}
            className="relative flex items-center justify-center"
            style={{ width: starSize, height: starSize }}
          >
            {(isFull || isHalf) && (
              <>
                <motion.div
                  className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.4, 0.9, 1.1, 1] }}
                  transition={{
                    duration: 0.7,
                    delay: delay,
                    times: [0, 0.3, 0.5, 0.7, 1],
                    ease: "easeInOut",
                  }}
                >
                  <GameStarSvg
                    fillPercent={fillPercent}
                    uniqueId={`star-${index}`}
                  />
                </motion.div>

                {/* Particle 1 */}
                <motion.div
                  className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{
                    x: -25,
                    y: -25,
                    scale: [0, 1.5, 0],
                    opacity: [1, 1, 0],
                  }}
                  transition={{
                    duration: 0.6,
                    delay: delay + 0.1,
                    ease: "easeOut",
                  }}
                />
                {/* Particle 2 */}
                <motion.div
                  className="absolute w-2.5 h-2.5 bg-yellow-200 rounded-full"
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{
                    x: 30,
                    y: -15,
                    scale: [0, 1.2, 0],
                    opacity: [1, 1, 0],
                  }}
                  transition={{
                    duration: 0.5,
                    delay: delay + 0.15,
                    ease: "easeOut",
                  }}
                />
              </>
            )}
            {!isFull && !isHalf && (
              <motion.div
                className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10"
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 0.9, 1] }}
                transition={{
                  duration: 0.5,
                  delay: delay,
                  times: [0, 0.4, 0.7, 1],
                  ease: "easeInOut",
                }}
              >
                <GameStarSvg fillPercent={0} uniqueId={`star-${index}`} />
              </motion.div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};
